'use client';

import { useFormTemplateKey } from '@commenda/api/form-builder/form-template-keys';
import { useFormsTemplate } from '@commenda/api/form-builder/forms-templates';
import {
  Badge,
  Input,
  Label,
  LoadingIndicator,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@commenda/kakashi';
import type { FormDSL } from '@commenda/types';
import { Info } from 'lucide-react';
import { useMemo } from 'react';
import { getFieldsFromTemplateDSL } from '../../utils/dsl-traversal';
import { previewEntryTitleTemplate } from '../../utils/entry-title';
import { EntryTitleTemplateField } from './EntryTitleTemplateField';
import { KeySelector } from './KeySelector';

const ROLE_OPTIONS = [
  { value: 'President', label: 'President' },
  { value: 'VicePresident', label: 'Vice President' },
  { value: 'Secretary', label: 'Secretary' },
  { value: 'Treasurer', label: 'Treasurer' },
  { value: 'ResponsibleParty', label: 'Responsible Party' },
] as const;

export function RoleAssignmentEditor({
  props,
  onUpdateProps,
  dsl,
}: {
  props: Record<string, unknown>;
  onUpdateProps: (key: string, value: unknown) => void;
  dsl: FormDSL | null;
}) {
  const role = (props.role as string) || '';
  const keyId = (props.keyId as string) || '';
  const { key } = useFormTemplateKey(keyId || null);
  const { template, isLoading: isLoadingTemplate } = useFormsTemplate(
    key?.liveFormId ?? null
  );

  const linkedFields = useMemo(() => {
    if (!template?.dsl) return [];
    return getFieldsFromTemplateDSL(template.dsl);
  }, [template?.dsl]);

  const linkedFormEntryTitleTemplate =
    template?.dsl?.form?.settings?.display?.entryTitleTemplate;

  // Design-time: warn if same role is used in another RoleAssignment node
  const duplicateRoleFieldIds = useMemo(() => {
    if (!dsl || !role) return [];
    const ids: string[] = [];
    dsl.steps?.forEach((step) => {
      step.modules?.forEach((mod) => {
        mod.components?.forEach((comp) => {
          if (
            comp.component === 'RoleAssignment' &&
            (comp.props as Record<string, unknown>)?.role === role
          ) {
            ids.push(comp.fieldId);
          }
        });
      });
    });
    return ids;
  }, [dsl, role]);

  return (
    <div className="space-y-4">
      <h3 className="txt-small-medium text-text-secondary">Role Assignment</h3>

      <div className="space-y-2">
        <Label>Role (required)</Label>
        <Select
          value={role || undefined}
          onValueChange={(v) => onUpdateProps('role', v || '')}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select role" />
          </SelectTrigger>
          <SelectContent>
            {ROLE_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Form template (required)</Label>
        <KeySelector
          value={keyId}
          onValueChange={(v) => onUpdateProps('keyId', v)}
          placeholder="Select a form template"
        />
        <p className="txt-xs-regular text-text-tertiary">
          The form that opens for the selected or new key person to collect
          role-specific info.
        </p>
      </div>

      {keyId && (
        <div className="space-y-2">
          {isLoadingTemplate ? (
            <div className="flex items-center gap-2 py-1">
              <LoadingIndicator />
              <span className="txt-xs-regular text-text-tertiary">
                Loading fields...
              </span>
            </div>
          ) : linkedFields.length > 0 ? (
            <div className="p-3 bg-bg-secondary rounded-md border border-border-secondary">
              <p className="txt-xs-regular text-text-tertiary mb-2">
                <Info className="inline-block w-3 h-3 mr-1" />
                Linked form collects {linkedFields.length} field
                {linkedFields.length !== 1 ? 's' : ''}:
              </p>
              <div className="flex flex-wrap gap-1">
                {linkedFields.map((f) => (
                  <Badge key={f.fieldId} variant="gray" className="text-xs">
                    {f.label}
                  </Badge>
                ))}
              </div>
            </div>
          ) : (
            <p className="txt-xs-regular text-text-tertiary">
              No fields found in the linked form
            </p>
          )}
        </div>
      )}

      {keyId && (
        <EntryTitleTemplateField
          label="Card title override"
          description={
            linkedFormEntryTitleTemplate
              ? `Leave empty to use the linked form's own setting: ${previewEntryTitleTemplate(linkedFormEntryTitleTemplate, linkedFields)}`
              : "The linked form has no entry title configured — set one here, or in that form's settings."
          }
          value={(props.entryTitleTemplate as string) || ''}
          fields={linkedFields}
          onChange={(value) =>
            onUpdateProps('entryTitleTemplate', value || undefined)
          }
        />
      )}

      {duplicateRoleFieldIds.length > 1 && (
        <div className="p-3 bg-amber-50 dark:bg-amber-950/30 rounded-md border border-amber-200 dark:border-amber-800">
          <p className="txt-xs-regular text-amber-800 dark:text-amber-200">
            <Info className="inline-block w-3 h-3 mr-1" />
            This role is used in more than one Role Assignment node. Only one
            person can hold this role; the last submitted value will apply.
          </p>
        </div>
      )}

      <div className="space-y-2">
        <Label>Add new label</Label>
        <Input
          value={(props.addNewLabel as string) || ''}
          onChange={(e) => onUpdateProps('addNewLabel', e.target.value)}
          placeholder="Add New Key Person"
        />
      </div>
    </div>
  );
}
